import {
  Button,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterButton } from "../Primitives/Buttons";
import Scrollbar from "../Scrollbar";
import SearchField from "../SearchField";

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(2),
    minHeight: 300,
    minWidth: 300,
    display: "flex",
    flexDirection: "column",
  },
}));

export interface IOption<T extends string | number = string | number> {
  value: T;
  displayValue: string;
}

interface IChipFilterProps<T extends string | number = string | number> {
  possibleOptions: IOption<T>[];
  label: string;
  filter: T | T[] | undefined;
  setFilter: (x: T | undefined) => void;
  onClose?: () => void;
  onChipClick?: (x: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: "default" | "outlined";
  returnFullOption?: boolean;
  multiple?: boolean;
  hideCheckbox?: boolean;
}

export const ChipFilter = <T extends string | number>(props: IChipFilterProps<T>) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { possibleOptions, label, filter, setFilter, onClose, onChipClick, returnFullOption, multiple, hideCheckbox } =
    props;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsActive(true);
    setAnchorEl(event.currentTarget);
    if (onChipClick) {
      onChipClick(event);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsActive(false);
    if (onClose) {
      onClose();
    }
  };

  const isChecked = (option: IOption<T>): boolean => {
    if (!filter) {
      return false;
    }
    if (returnFullOption === true) {
      const checked = multiple ? (filter as T[]).includes(option.value) : option.value === (filter as T);
      return checked;
    } else {
      return multiple ? (filter as T[]).includes(option.value) : option.value === (filter as T);
    }
  };

  const filterOptions = (options: IOption<T>[]) => {
    let filteredOptions = options
      .filter((option) => option.value && option.displayValue)
      .sort((a, _b) => (a.value === filter ? -1 : 1));

    if (searchTerm) {
      filteredOptions = filteredOptions.filter((option) =>
        t(option.displayValue ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    }
    return filteredOptions;
  };

  const open = Boolean(anchorEl);
  return (
    <div>
      <FilterButton
        isActive={isActive}
        isFilterActive={filterOptions(possibleOptions).some((option) => isChecked(option))}
        onClick={handleClick}
      >
        {label}
      </FilterButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div className={classes.container}>
          {
            <div>
              <SearchField
                onSearchSubmit={(value: string) => {
                  setSearchTerm(value);
                }}
                placeholder={t("general.search")}
                initialValue={searchTerm}
              />

              <Scrollbar style={{ minHeight: 250, marginTop: 18, marginBottom: 18 }}>
                <List>
                  {filterOptions(possibleOptions).map((option: IOption<T>, index: number) => {
                    return (
                      <div key={index}>
                        <ListItemButton
                          dense
                          onClick={() => {
                            if (filter === option.value) {
                              setFilter(undefined);
                            } else {
                              setFilter(returnFullOption === true ? (option as unknown as T) : option.value);
                            }
                            if (!multiple) {
                              handleClose();
                            }
                          }}
                        >
                          {!hideCheckbox && (
                            <ListItemIcon>
                              <Checkbox edge="start" checked={isChecked(option)} tabIndex={-1} disableRipple />
                            </ListItemIcon>
                          )}
                          <ListItemText primary={t(option.displayValue)} />
                        </ListItemButton>
                        <Divider />
                      </div>
                    );
                  })}
                </List>
              </Scrollbar>
            </div>
          }

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Button onClick={handleClose} variant="contained" fullWidth color="secondary">
              <Typography>{t("general.close")}</Typography>
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};
